import React, { useEffect, useState, useMemo } from "react";
import FullPageSpinner from "../layout/FullPageSpinner";
import ConfirmModal from "../layout/ConfirmModal";
import AdminEditModal from "./AdminEditModal";
import {
  fetchAllAdmins,
  createAdminVendor,
  updateAdminVendor,
  deleteAdminVendor,
  fetchAllVendors,
} from "../../actions/admin";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { ROLES } from "../../utils/constants";

const AdminManagement = ({ vendor }) => {
  const { user } = useSelector((state) => state.auth);
  const [admins, setAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showModal, setShowModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const hasPermission = (permission) => {
    if (user?.role === ROLES.SUPER_ADMIN) return true;
    return user?.permissions?.[permission] === true;
  };

  useEffect(() => {
    if (!hasPermission('admin_view')) {
      setAdmins([]);
      return;
    }

    const getAdmins = async () => {
      setLoading(true);
      try {
        const response = await fetchAllAdmins();
        if (response.success) {
          setAdmins(Array.isArray(response?.admins) ? response?.admins : []);
        } else {
          setAdmins([]);
        }
      } catch (error) {
        setAdmins([]);
      } finally {
        setLoading(false);
      }
    };

    const getVendors = async () => {
      setLoading(true);
      try {
        const response = await fetchAllVendors();
        if (response.success) {
          setAdmins(Array.isArray(response?.vendors) ? response?.vendors : []);
        } else {
          setAdmins([]);
        }
      } catch (error) {
        setAdmins([]);
      } finally {
        setLoading(false);
      }
    };
    if (vendor) {
      getVendors();
    } else {
      getAdmins();
    }
  }, [vendor]);

  const onEditAdminClick = (admin) => {
    setSelectedAdmin(admin);
    setSelectedId(admin.id);
    setShowModal(true);
  };

  const onDeleteAdminClick = (id, role) => {
    let deletionAllowed = true;
    if (role === ROLES.ADMIN) {
      const checkAdminsOnly = admins.filter(
        (admin) => admin.role === ROLES.ADMIN
      );
      deletionAllowed = checkAdminsOnly.length > 1;
    }

    if (deletionAllowed) {
      setSelectedId(id);
      setShowConfirm(true);
    } else {
      toast.error("At least one admin should be present!", {
        position: "top-right",
      });
    }
  };

  const deleteConfirmClick = async () => {
    setLoading(true);
    await deleteAdminVendor(selectedId);
    setShowConfirm(false);
    setLoading(false);
    setAdmins(admins.filter((admin) => admin.id !== selectedId));
  };

  const handleSave = async (formData) => {
    setLoading(true);
    
    try {
      let response;
      if (selectedId) {
        response = await updateAdminVendor(formData, selectedId);
      } else {
        response = await createAdminVendor(formData);
      }

      if (response && response.success) {
        const fetchResponse = vendor
          ? await fetchAllVendors()
          : await fetchAllAdmins();
        if (fetchResponse && fetchResponse.success) {
          setAdmins(
            Array.isArray(vendor ? fetchResponse.vendors : fetchResponse.admins)
              ? vendor
                ? fetchResponse.vendors
                : fetchResponse.admins
              : []
          );
        }
        setShowModal(false);
        setSelectedId(null);
        setSelectedAdmin(null);
      }
    } catch (error) {
      console.error("Error saving admin/vendor:", error);
    } finally {
      setLoading(false);
    }
  };

  const addAdmin = () => {
    setSelectedId(null);
    setSelectedAdmin(null);
    setShowModal(true);
  };

  const filteredAdmins = useMemo(() => {
    return admins.filter((admin) =>
      admin.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [admins, searchTerm]);

  const totalPages = Math.ceil(filteredAdmins.length / itemsPerPage);
  const paginatedAdmins = filteredAdmins.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <>
      <style>{`
        .cursor-pointer { cursor: pointer; }
        .table-responsive::-webkit-scrollbar { height: 8px; }
        .table-responsive::-webkit-scrollbar-thumb { background: #dee2e6; border-radius: 4px; }
        .filter-card { transition: all 0.3s ease; }
        .action-btn { transition: transform 0.2s; white-space: nowrap; }
        .action-btn:hover { transform: translateY(-1px); }
        .table th { white-space: nowrap; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.5px; }
        .table td { font-size: 0.9rem; }
        .pagination-container { border-top: 1px solid #dee2e6; padding-top: 1.5rem; }
      `}</style>
      {loading && <FullPageSpinner loading={loading} />}
      <div className="bg-light min-vh-100 py-4">
        <div className="container-fluid px-lg-4">
          <div className="card shadow-sm border-0 rounded-3">
            <div className="card-header bg-white border-bottom py-3 px-4">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <h4 className="mb-0 text-primary fw-bold d-flex align-items-center">
                  <i className="bi bi-person-badge-fill me-2"></i>Member Management
                </h4>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-primary rounded-pill px-3 py-2">Total: {filteredAdmins.length}</span>
                </div>
              </div>
            </div>
            
            <div className="card-body p-4">
              {/* Filters & Actions Section */}
              <div className="bg-white rounded-3 p-4 mb-4 border shadow-sm filter-card">
                <div className="row g-3 align-items-end mb-4">
                  <div className="col-12 col-md-8 col-lg-6">
                    <label className="form-label small fw-bold text-muted text-uppercase mb-1">
                      Search Members
                    </label>
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-white border-end-0">
                        <i className="bi bi-search text-muted"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control border-start-0 ps-0"
                        placeholder="Search by name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="col-12 col-md-4 col-lg-6 text-md-end">
                    {hasPermission('admin_add') && (
                      <button
                        className="btn btn-success btn-sm action-btn px-4 fw-bold shadow-sm"
                        onClick={addAdmin}
                      >
                        <i className="bi bi-person-plus-fill me-1"></i> Add Member
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Table Section */}
              <div className="table-responsive rounded-3 border shadow-sm bg-white">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Contact Number</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedAdmins.length > 0 ? (
                      paginatedAdmins.map((admin) => (
                        <tr key={admin.id}>
                          <td>
                            <div className="fw-semibold text-dark">{admin.name}</div>
                          </td>
                          <td className="small">{admin.email}</td>
                          <td className="small">{admin.phoneNumber}</td>
                          <td>
                            <span className="badge bg-light text-dark border text-capitalize px-3">{admin.role}</span>
                          </td>
                          <td> 
                            <span className={`badge rounded-pill ${admin?.isActive ? 'bg-success text-white' : 'bg-secondary text-white'}`}>
                              {admin?.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-1">
                              {hasPermission('admin_edit') && (
                                <button
                                  className="btn btn-outline-primary btn-sm rounded-circle"
                                  onClick={() => onEditAdminClick(admin)}
                                  title="Edit Member"
                                  style={{ width: '32px', height: '32px', padding: 0 }}
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                              )}
                              {hasPermission('admin_delete') && (
                                <button
                                  className="btn btn-outline-danger btn-sm rounded-circle"
                                  onClick={() => onDeleteAdminClick(admin.id, admin.role)}
                                  title="Delete Member"
                                  style={{ width: '32px', height: '32px', padding: 0 }}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-5 text-muted">
                          <i className="bi bi-people-fill fs-1 d-block mb-2"></i>
                          No members found matching your criteria.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Section */}
              <div className="pagination-container d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 gap-3">
                <div className="d-flex align-items-center gap-3 order-2 order-md-1">
                  <div className="d-flex align-items-center bg-white border rounded px-2 py-1 shadow-sm">
                    <span className="text-muted small me-2">Show</span>
                    <select
                      value={10} // itemsPerPage is hardcoded in component
                      disabled
                      className="form-select form-select-sm border-0 bg-transparent fw-bold"
                      style={{ width: 'auto', outline: 'none', boxShadow: 'none' }}
                    >
                      <option value={10}>10</option>
                    </select>
                    <span className="text-muted small ms-2">entries</span>
                  </div>
                  <div className="text-muted small border-start ps-3 d-none d-sm-block">
                    Showing <span className="fw-bold text-dark">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="fw-bold text-dark">{Math.min(currentPage * itemsPerPage, filteredAdmins.length)}</span> of <span className="fw-bold text-dark">{filteredAdmins.length}</span> records
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2 order-1 order-md-2">
                  <button
                    className="btn btn-outline-primary btn-sm px-3 rounded-pill shadow-sm fw-bold"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => prev - 1)}
                  >
                    <i className="bi bi-chevron-left"></i> Prev
                  </button>
                  
                  <div className="bg-white px-3 py-1 rounded-pill border shadow-sm small fw-bold">
                    Page {currentPage} of {totalPages}
                  </div>

                  <button
                    className="btn btn-outline-primary btn-sm px-3 rounded-pill shadow-sm fw-bold"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                  >
                    Next <i className="bi bi-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {showModal && (
        <AdminEditModal
          show={showModal}
          admin={selectedAdmin}
          vendor={vendor}
          onSave={handleSave}
          onHide={() => {
            setShowModal(false);
            setSelectedAdmin(null);
            setSelectedId(null);
          }}
        />
      )}

      <ConfirmModal
        show={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={deleteConfirmClick}
        message="Are you sure you want to delete this member?"
      />
    </>
  );
};

export default AdminManagement;
